import { useQuery } from '@tanstack/react-query'
import { nothing, produce } from 'immer'
import { UserGroup } from '@opallabs/concepts'
import {
  userGroups,
  OpalApiRequestParams,
  findResourceOfType,
  OpalApiComponent,
  OpalApiResponseDocument,
} from '../../../../v3'
import { globalQueryKeys as queryKeys } from '../../queryKeys'
import { filterAndSortGroups } from './helpers'

type ResponseDocument = OpalApiResponseDocument<'ReadUserGroupsV3', 200>
type ReadUserGroupsInclude =
  OpalApiRequestParams<'ReadUserGroupsV3'>['query']['include']
type UserGroupsFilter =
  OpalApiRequestParams<'ReadUserGroupsV3'>['query']['filter']

type UseGetGroupsParams<TUserGroup extends UserGroup> = {
  include?: ReadUserGroupsInclude
  enabled?: boolean
  filter?: UserGroupsFilter
  select: (document: ResponseDocument) => TUserGroup[]
}

const defaultInclude: ReadUserGroupsInclude = ['asset', 'members']

const sanitizeFilter = (
  filter: UserGroupsFilter | undefined
): UserGroupsFilter | undefined => {
  return produce(filter, (draftFilter) => {
    if (!draftFilter || typeof draftFilter.name !== 'string') {
      return
    }

    draftFilter.name = draftFilter.name.trim()

    if (draftFilter.name.length === 0) {
      delete draftFilter.name

      if (!Object.keys(draftFilter).length) {
        return nothing
      }
    }
  })
}

export const defaultUserGroupsSelector = (
  responseDocument: ResponseDocument,
  showArchived: boolean
) => {
  const visibleGroups = filterAndSortGroups(responseDocument, showArchived)

  return visibleGroups.map<UserGroup>((group) => {
    const groupAsset = responseDocument.included?.find(
      findResourceOfType('asset', 'V3', group.relationships.asset?.data?.id)
    )
    const groupMembers: Array<OpalApiComponent<'V3', 'user'>> = []
    group.relationships.members.data.forEach((user) => {
      const member = responseDocument.included?.find(
        findResourceOfType('user', 'V3', user.id)
      )
      if (member) {
        groupMembers.push(member)
      }
    })

    return {
      type: 'user_group',
      uuid: group.id,
      name: group.attributes.name,
      description: group.attributes.description ?? undefined,
      archived: Boolean(group.attributes.is_archived),
      asset: groupAsset
        ? {
            id: groupAsset.id,
            title: groupAsset.attributes.file_name,
            url: groupAsset.attributes.url,
          }
        : undefined,
      members: groupMembers.map((member) => ({
        uuid: member.id,
        name: member.attributes.full_name,
        type: 'user',
        legacyId: member.attributes.legacy_id,
      })),
    }
  })
}

export const useUserGroups = <TUserGroup extends UserGroup>({
  include = defaultInclude,
  filter,
  select,
  enabled = true,
}: UseGetGroupsParams<TUserGroup>) => {
  const sanitizedFilter = sanitizeFilter(filter)

  return useQuery({
    queryKey: queryKeys.groups(sanitizedFilter, include),
    queryFn: () =>
      userGroups.readCollection({
        params: {
          query: {
            include,
            filter: sanitizedFilter,
          },
        },
      }),
    enabled,
    retry: false,
    useErrorBoundary: true,
    select: select,
  })
}
